import React, { useCallback, useMemo } from 'react';

import { IItem } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import useEffectOnce from 'hooks/use-effect-once';
import { useEnableUnifiedMenu } from 'hooks/use-enable-unified-menu';
import { useFeatureDonation } from 'hooks/use-feature-donation';
import { CustomEventNames, EventTypes, logRBIEvent } from 'state/amplitude';
import { useMenuContext } from 'state/menu';
import useDonation from 'state/order/hooks/use-donation';
import { useUIContext } from 'state/ui';

import { DonationAtCheckout } from '../donation-at-checkout';
import { IDonationAtCheckoutProps } from '../donation-at-checkout/types';

interface IDonationContainerProps {
  isLoading: boolean;
}

const DonationContainer = ({ isLoading }: IDonationContainerProps) => {
  const { featureDonation, loading } = useFeatureDonation();
  const {
    pricingFunction,
    getPricingAndAvailability,
    getOneMenuItemAvailability,
  } = useMenuContext();
  const { formatCurrencyForLocale } = useUIContext();
  const isUnifiedMenuEnabled = useEnableUnifiedMenu();

  const donationItems = useMemo(
    () =>
      featureDonation?.donationItemList
        ?.filter(item =>
          isUnifiedMenuEnabled
            ? getOneMenuItemAvailability(item._id).availabilityStatus ===
              ItemAvailabilityStatus.AVAILABLE
            : getPricingAndAvailability(item._id)?.isAvailable
        )
        ?.map(item => ({ ...item } as IItem)) || [],
    [
      featureDonation?.donationItemList,
      getOneMenuItemAvailability,
      getPricingAndAvailability,
      isUnifiedMenuEnabled,
    ]
  );

  const {
    handleAddDonationCartEntry,
    handleRemoveDonationCartEntry,
    selectedDonationCartEntry,
  } = useDonation({
    skipRemove: loading,
    donationItems,
  });

  const allDonationOptions: IDonationAtCheckoutProps['donationOptions'] = useMemo(
    () => [
      ...(donationItems.map(item => ({
        label: formatCurrencyForLocale(pricingFunction({ item })),
        value: item._id,
        isDisabled: false,
      })) as IDonationAtCheckoutProps['donationOptions']),
    ],
    [donationItems, formatCurrencyForLocale, pricingFunction]
  );

  const handleDonationToggleChange = useCallback(
    (newSelectedDonationId: string) => {
      if (selectedDonationCartEntry?._id === newSelectedDonationId) {
        handleRemoveDonationCartEntry({ cartId: selectedDonationCartEntry.cartId });
        return;
      }

      const optionItem = donationItems.find(item => item?._id === newSelectedDonationId);
      if (!optionItem) {
        return;
      }
      handleAddDonationCartEntry(optionItem);
    },
    [
      donationItems,
      handleAddDonationCartEntry,
      handleRemoveDonationCartEntry,
      selectedDonationCartEntry,
    ]
  );

  const haveAllItemsZeroPrice = (donationItems: IItem[]): boolean => {
    const total = donationItems.reduce(
      (accumulator, currentItem) => accumulator + pricingFunction({ item: currentItem }),
      0
    );

    return total <= 0;
  };

  useEffectOnce(() => {
    if (donationItems.length) {
      logRBIEvent({
        name: CustomEventNames.FOUNDATION_DONATIONS_VIEW,
        type: EventTypes.Other,
        attributes: {},
      });
    }
  });

  if (!donationItems.length || haveAllItemsZeroPrice(donationItems)) {
    return null;
  }

  return (
    <DonationAtCheckout
      isLoading={isLoading}
      header={featureDonation?.header?.locale ?? ''}
      subtitle={featureDonation?.subtitle?.locale ?? ''}
      donationOptions={allDonationOptions}
      selectedDonationId={selectedDonationCartEntry?._id}
      onChangeValue={handleDonationToggleChange}
    />
  );
};

export default DonationContainer;
